import { useEffect, useState } from "react";
import { bitable } from "@lark-base-open/js-sdk";

export interface BaseContext {
  appToken: string;
  userId: string;
  tenantKey: string | null;
}

export type BaseContextStatus =
  | { phase: "loading" }
  | { phase: "ready"; context: BaseContext }
  | { phase: "error"; message: string };

/**
 * 加载当前飞书多维表格上下文：app_token / user_id / tenant_key。
 * - app_token：来自 `bitable.base.getSelection().baseId`（SDK 把它叫 baseId）
 * - user_id / tenant_key：来自 `bitable.bridge`
 * 离开飞书 iframe 环境（例如直接打开 vite dev URL）时 SDK 会抛错，由调用方处理。
 */
export function useBaseContext(): BaseContextStatus {
  const [status, setStatus] = useState<BaseContextStatus>({ phase: "loading" });

  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const [selection, userId, tenantKey] = await Promise.all([
          bitable.base.getSelection(),
          bitable.bridge.getUserId(),
          (async () => {
            try {
              return await bitable.bridge.getTenantKey();
            } catch {
              return null;
            }
          })(),
        ]);
        const appToken = selection?.baseId ?? null;
        if (!appToken || !userId) {
          throw new Error("SDK 返回的 baseId/userId 为空，请确认插件运行在飞书多维表格 iframe 内");
        }
        if (!cancelled) {
          setStatus({
            phase: "ready",
            context: { appToken, userId, tenantKey: tenantKey ?? null },
          });
        }
      } catch (err) {
        if (!cancelled) {
          setStatus({
            phase: "error",
            message: (err as Error).message ?? "SDK 上下文加载失败",
          });
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return status;
}
